import {
    DragDropContext,
    DragUpdate,
    DraggableId,
    DraggableLocation,
    DropResult,
} from "@hello-pangea/dnd";
import { Box, styled } from "@mui/material";
import { uniqBy } from "lodash";
import { useCallback, useState } from "react";
import { IListItem } from "../../interface/list-item";
import { ITreeItem } from "../../interface/tree-item";
import { IWithClassName, IWithThemeProps } from "../../theme/default.theme";
import { flatOptions } from "../../util/flat-list";
import ControllerBox from "./controller-box";
import { BoxHeaderName } from "./enum/box-header";
import OptionBox from "./option-box";

interface IProps extends IWithClassName {
    options: Array<ITreeItem>;
    selected: Array<IListItem>;
    onChange: (list: Array<IListItem>) => void;
    isTreeView?: boolean;
    leftBoxHeader: string;
    rightBoxHeader: string;
    error?: string;
    addTitle?: string;
    removeTitle?: string;
    addAllTitle?: string;
    removeAllTitle?: string;
    disabled?: boolean;
}

const DraggableSelector = ({
    className,
    options,
    selected,
    isTreeView,
    onChange,
    leftBoxHeader,
    rightBoxHeader,
    error,
    addTitle,
    removeTitle,
    addAllTitle,
    removeAllTitle,
    disabled,
}: IProps) => {
    const [rightBoxHighlighted, setRightBoxHighlighted] = useState<
        Array<IListItem>
    >([]);
    const [leftBoxHighlighted, setLeftBoxHighlighted] = useState<
        Array<IListItem>
    >([]);
    const [leftDraggerInTransition, setLeftDraggerInTransition] = useState<
        null | string
    >(null);
    const [rightDraggerInTransition, setRightDraggerInTransition] = useState<
        null | string
    >(null);

    const onCheckBoxChange =
        (box: BoxHeaderName) => (isChecked: boolean, item: IListItem) => {
            const callBackFunction =
                box === BoxHeaderName.LEFT_BOX
                    ? setLeftBoxHighlighted
                    : setRightBoxHighlighted;

            if (isChecked) {
                callBackFunction((values) => uniqBy([...values, item], "id"));
            } else {
                callBackFunction((values) =>
                    values.splice(values.indexOf(item), 1),
                );
            }
        };

    const onSelect = () => {
        onChange([...selected, ...rightBoxHighlighted]);
        setRightBoxHighlighted([]);
    };

    const onUnselect = () => {
        onChange(
            selected.filter(
                ({ id }) => !leftBoxHighlighted.find((list) => list.id === id),
            ),
        );
        setLeftBoxHighlighted([]);
    };

    const onUnselectAll = () => {
        onChange([]);
    };

    const onSelectAll = () => {
        onChange(flatOptions(options));
    };

    const filteredOptions = useCallback(
        (options: Array<ITreeItem>) => {
            const resultObj: Array<ITreeItem> = [];

            options.forEach((option) => {
                if (option.children) {
                    const filterdClildItems = filteredOptions(
                        option.children,
                    ) as Array<IListItem>;

                    if (filterdClildItems) {
                        resultObj.push({
                            ...option,
                            children: filterdClildItems,
                        });
                    }
                } else if (!selected.find(({ id }) => id === option.id)) {
                    resultObj.push(option);
                }
            });

            return resultObj;
        },
        [options, selected],
    );

    const moveArrayItem = (
        arr: Array<IListItem>,
        oldIndex: number,
        newIndex: number,
    ) => {
        if (newIndex >= arr.length) {
            let index = newIndex - arr.length + 1;
            while (index--) {
                arr.push();
            }
        }

        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);

        return arr;
    };

    const onDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;

        setLeftDraggerInTransition(null);
        setRightDraggerInTransition(null);

        if (!destination) return;

        const isSameDroppable = source.droppableId === destination.droppableId;

        if (destination.droppableId === BoxHeaderName.LEFT_BOX) {
            if (isSameDroppable) {
                handleReorderInLeftBox(source, destination);
            } else if (
                source.droppableId === BoxHeaderName.RIGHT_BOX ||
                source.droppableId.includes(BoxHeaderName.PARENT)
            ) {
                handleMoveToLeftBox(draggableId);
            }
        } else if (
            destination.droppableId === BoxHeaderName.RIGHT_BOX &&
            source.droppableId === BoxHeaderName.LEFT_BOX
        ) {
            handleMoveToRightBox(draggableId);
        }
    };

    const handleReorderInLeftBox = (
        source: DraggableLocation,
        destination: DraggableLocation,
    ) => {
        onChange(moveArrayItem(selected, source.index, destination.index));
    };

    const handleMoveToLeftBox = (draggableId: DraggableId) => {
        if (draggableId.includes(BoxHeaderName.PARENT)) {
            const parentId = draggableId.replace(BoxHeaderName.PARENT, "");
            const selectedOptions = options.find(({ id }) => parentId === id);

            if (selectedOptions) {
                onChange(
                    uniqBy(
                        [...selected, ...(selectedOptions.children ?? [])],
                        "id",
                    ),
                );
            }
        } else {
            const newSelect = flatOptions(options).find(
                ({ id }) => id === draggableId,
            );

            if (newSelect) {
                onChange([...selected, newSelect]);
            }
        }
    };

    const handleMoveToRightBox = (draggableId: DraggableId) => {
        onChange(selected.filter(({ id }) => id !== draggableId));
    };

    const onDragUpdate = (update: DragUpdate) => {
        const { destination, source, draggableId } = update;
        if (
            destination?.droppableId === BoxHeaderName.RIGHT_BOX &&
            source.droppableId === BoxHeaderName.LEFT_BOX
        ) {
            const info = flatOptions(options)?.find(
                ({ id }) => id === draggableId,
            ) as unknown as { parentId: string };
            setLeftDraggerInTransition(info?.parentId);
        } else if (source.droppableId.includes(BoxHeaderName.PARENT)) {
            setRightDraggerInTransition(draggableId);
        } else {
            setLeftDraggerInTransition(null);
        }
    };

    return (
        <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
            <Box className={className}>
                <OptionBox
                    title={leftBoxHeader}
                    data={selected}
                    onHighlight={onCheckBoxChange(BoxHeaderName.LEFT_BOX)}
                    droppableId={BoxHeaderName.LEFT_BOX}
                    isTreeView={false}
                    isSingleColumn={true}
                    error={error}
                    disabled={disabled}
                />
                <ControllerBox
                    onSelectAll={onSelectAll}
                    onUnselectAll={onUnselectAll}
                    onSelect={onSelect}
                    onUnselect={onUnselect}
                    addTitle={addTitle}
                    removeTitle={removeTitle}
                    addAllTitle={addAllTitle}
                    removeAllTitle={removeAllTitle}
                    disabled={disabled}
                />
                <OptionBox
                    title={rightBoxHeader}
                    data={filteredOptions(options)}
                    isTreeView={!!isTreeView}
                    onHighlight={onCheckBoxChange(BoxHeaderName.RIGHT_BOX)}
                    droppableId={BoxHeaderName.RIGHT_BOX}
                    isSingleColumn={false}
                    leftDraggerInTransition={leftDraggerInTransition}
                    rightDraggerInTransition={rightDraggerInTransition}
                    disabled={disabled}
                />
            </Box>
        </DragDropContext>
    );
};

export default styled(DraggableSelector)(({ theme }: IWithThemeProps) => ({
    display: "flex",
    gap: theme?.spacing(1.2),
    li: {
        listStyle: "none",
        textAlign: "left",
    },
}));
